import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Briefcase, FileText, Link as LinkIcon, X, Save, Users as UsersIcon, TrendingUp, CheckCircle2, AlertCircle } from "lucide-react";

export default function Positions() {
  const [positions, setPositions] = useState([]);
  const [users, setUsers] = useState([]);
  const [openEdit, setOpenEdit] = useState(null); // position object or "new"

  const load = () => api.get("/positions").then(({ data }) => setPositions(data));
  const loadUsers = () => api.get("/users").then(({ data }) => setUsers(data));

  useEffect(() => {
    load();
    loadUsers();
  }, []);

  const remove = async (p) => {
    const holders = users.filter((u) => u.position_id === p.id);
    const msg = holders.length
      ? `Delete "${p.name}"? ${holders.length} teammate${holders.length === 1 ? "" : "s"} currently hold this position and will be unassigned.`
      : `Delete "${p.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/positions/${p.id}`);
      toast.success(`Deleted "${p.name}"`);
      load();
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-8" data-testid="positions-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--accent)]">Admin</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Positions &amp; Agreements</h1>
          <p className="text-sm text-slate-400 mt-2 max-w-xl">
            Define positions your team holds (Mod, Admin, VP, CIO, CMO…), attach a staff agreement describing the duties, then assign a position to any teammate from the directory.
          </p>
        </div>
        <button
          onClick={() => setOpenEdit("new")}
          data-testid="new-position-btn"
          className="px-4 py-2.5 bg-[color:var(--accent)] text-white rounded-lg font-bold flex items-center gap-2 hover:bg-[color:var(--accent-hover)] transition-colors self-start"
        >
          <Plus size={16} /> New position
        </button>
      </div>

      <PositionAnalytics positions={positions} users={users} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="positions-list">
        {positions.length === 0 && (
          <div className="col-span-full card-surface p-8 text-center text-slate-500 text-sm">
            No positions defined yet. Click <strong className="text-slate-300">New position</strong> to add your first role.
          </div>
        )}
        {positions.map((p) => {
          const holders = users.filter((u) => u.position_id === p.id);
          return (
            <div key={p.id} className="card-surface admin-card p-5" data-testid={`position-card-${p.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Briefcase size={16} className="text-[color:var(--accent)] shrink-0" />
                  <h3 className="text-lg font-bold truncate">{p.name}</h3>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => setOpenEdit(p)}
                    data-testid={`position-edit-${p.id}`}
                    className="p-1.5 rounded hover:bg-[#242c3d] text-slate-400 hover:text-[color:var(--accent)] transition-colors"
                    title="Edit"
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    onClick={() => remove(p)}
                    data-testid={`position-delete-${p.id}`}
                    className="p-1.5 rounded hover:bg-[#242c3d] text-slate-400 hover:text-[#ff3b5c] transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              {p.description && <p className="text-sm text-slate-300 mb-3 line-clamp-2">{p.description}</p>}
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1">
                  <UsersIcon size={12} /> {holders.length} teammate{holders.length === 1 ? "" : "s"}
                </span>
                {p.agreement_body && (
                  <span className="inline-flex items-center gap-1 text-slate-400">
                    <FileText size={12} /> Agreement attached
                  </span>
                )}
                {p.agreement_url && (
                  <a
                    href={p.agreement_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[color:var(--accent)] hover:underline"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`position-external-${p.id}`}
                  >
                    <LinkIcon size={12} /> External
                  </a>
                )}
              </div>
              {holders.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-800 flex -space-x-2">
                  {holders.slice(0, 6).map((u) => {
                    const initials = (u.name || u.email).split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
                    return (
                      <div key={u.id} title={u.name} className="w-7 h-7 rounded-full border-2 border-[#121620] bg-[#242c3d] flex items-center justify-center text-[10px] font-bold text-[color:var(--accent)] overflow-hidden">
                        {u.avatar_url ? <img src={u.avatar_url} alt={u.name} className="w-full h-full object-cover" /> : initials}
                      </div>
                    );
                  })}
                  {holders.length > 6 && (
                    <div className="w-7 h-7 rounded-full border-2 border-[#121620] bg-[#242c3d] flex items-center justify-center text-[10px] font-bold text-slate-300">
                      +{holders.length - 6}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {openEdit && (
        <PositionModal
          position={openEdit === "new" ? null : openEdit}
          onClose={() => setOpenEdit(null)}
          onSaved={() => { setOpenEdit(null); load(); }}
        />
      )}
    </div>
  );
}

// ---------- Position Analytics ----------
function PositionAnalytics({ positions, users }) {
  if (positions.length === 0) return null;

  const now = new Date();
  const MONTHS_BACK = 6;
  // Build 6 month-end buckets (oldest → newest)
  const buckets = [];
  for (let i = MONTHS_BACK - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i + 1, 0, 23, 59, 59); // last day of that month
    buckets.push(d);
  }

  // For each position, compute [count at bucket_0, ..., count at bucket_5]
  const rows = positions.map((p) => {
    const holders = users.filter((u) => u.position_id === p.id);
    const counts = buckets.map((endOfMonth) => {
      return holders.filter((u) => {
        if (!u.position_assigned_at) return true; // treat legacy/no-timestamp assignments as always held
        return new Date(u.position_assigned_at) <= endOfMonth;
      }).length;
    });
    const acknowledged = holders.filter((u) => u.position_acknowledged_at).length;
    return {
      id: p.id,
      name: p.name,
      current: holders.length,
      counts,
      acknowledged,
      hasAgreement: !!(p.agreement_body || p.agreement_url),
    };
  });

  const totalCurrent = rows.reduce((sum, r) => sum + r.current, 0);
  const maxCount = Math.max(1, ...rows.flatMap((r) => r.counts));
  const monthLabels = buckets.map((d) => d.toLocaleString(undefined, { month: "short" }));

  return (
    <div className="card-surface admin-card p-5" data-testid="position-analytics">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp size={16} className="text-[color:var(--accent)]" />
        <h2 className="text-lg font-bold">Headcount over time</h2>
        <span className="text-xs text-slate-500">· last {MONTHS_BACK} months · {totalCurrent} teammate{totalCurrent === 1 ? "" : "s"} assigned</span>
      </div>
      {totalCurrent === 0 ? (
        <div className="text-sm text-slate-500 py-6 text-center">
          No teammates assigned to any position yet. Head to the Directory to make your first assignment.
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((r) => {
            const ackRate = r.current > 0 ? Math.round((r.acknowledged / r.current) * 100) : 0;
            return (
              <div key={r.id} className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 sm:gap-6 items-center" data-testid={`analytics-row-${r.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="text-sm font-semibold truncate">{r.name}</div>
                    <span className="text-xs text-slate-500">· {r.current}</span>
                    {r.hasAgreement && (
                      <span
                        title={`${r.acknowledged} / ${r.current} teammates have acknowledged`}
                        className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${
                          ackRate === 100
                            ? "bg-[#00e5ff20] text-[#00e5ff]"
                            : ackRate >= 50
                            ? "bg-[#ffc70020] text-[#ffc700]"
                            : "bg-[#ff3b5c20] text-[#ff3b5c]"
                        }`}
                      >
                        {ackRate}% ack
                      </span>
                    )}
                  </div>
                  <Sparkline counts={r.counts} max={maxCount} labels={monthLabels} />
                </div>
                <div className="text-right shrink-0">
                  <div className="text-3xl font-extrabold text-[color:var(--accent)] leading-none">{r.current}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">current</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Sparkline({ counts, max, labels }) {
  // Simple inline SVG line + area, ~180x40 for compact display
  const W = 260;
  const H = 40;
  const stepX = counts.length > 1 ? W / (counts.length - 1) : 0;
  const points = counts.map((c, i) => {
    const x = i * stepX;
    const y = H - (c / max) * (H - 6) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const areaPoints = `0,${H} ${points} ${W},${H}`;
  return (
    <div className="relative" data-testid="sparkline">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" className="overflow-visible">
        <polygon points={areaPoints} fill="rgba(255,0,0,0.14)" />
        <polyline
          points={points}
          fill="none"
          stroke="var(--accent)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {counts.map((c, i) => {
          const x = i * stepX;
          const y = H - (c / max) * (H - 6) - 2;
          return <circle key={`pt-${labels[i]}`} cx={x} cy={y} r={2.2} fill="var(--accent)" />;
        })}
      </svg>
      <div className="flex justify-between text-[9px] uppercase tracking-widest text-slate-500 mt-0.5">
        {labels.map((l, i) => <span key={`lbl-${l}-${i}`}>{l}</span>)}
      </div>
    </div>
  );
}

function PositionModal({ position, onClose, onSaved }) {
  const isNew = !position;
  const [f, setF] = useState({
    name: position?.name || "",
    description: position?.description || "",
    agreement_body: position?.agreement_body || "",
    agreement_url: position?.agreement_url || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      if (isNew) {
        await api.post("/positions", f);
        toast.success(`Created "${f.name}"`);
      } else {
        await api.patch(`/positions/${position.id}`, f);
        toast.success("Position updated");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4 animate-fade-in" onClick={onClose}>
      <form
        onSubmit={submit}
        className="card-surface p-6 w-full max-w-2xl fade-in-up max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="position-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Briefcase size={18} className="text-[color:var(--accent)]" />
            <h2 className="text-xl font-bold">{isNew ? "Add position" : `Edit "${position.name}"`}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-200" data-testid="position-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4">
          <label className="block">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Name *</div>
            <input
              value={f.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. Community Moderator"
              required
              data-testid="pos-name"
              className="input"
            />
          </label>
          <label className="block">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Short description</div>
            <input
              value={f.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="One-liner shown on the position card"
              data-testid="pos-description"
              className="input"
            />
          </label>
          <label className="block">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-1 flex items-center gap-1.5">
              <FileText size={12} /> Staff agreement — duties &amp; responsibilities
            </div>
            <textarea
              value={f.agreement_body}
              onChange={(e) => set("agreement_body", e.target.value)}
              rows={10}
              placeholder="Paste or write the full staff agreement — duties, responsibilities, code of conduct, reporting lines. Teammates assigned to this position will see this text on their profile."
              data-testid="pos-agreement-body"
              className="input resize-y font-mono text-[13px] leading-relaxed"
            />
          </label>
          <label className="block">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-1 flex items-center gap-1.5">
              <LinkIcon size={12} /> External document URL (optional)
            </div>
            <input
              type="url"
              value={f.agreement_url}
              onChange={(e) => set("agreement_url", e.target.value)}
              placeholder="https://docs.google.com/… or a PDF link"
              data-testid="pos-agreement-url"
              className="input"
            />
            <div className="text-[11px] text-slate-500 mt-1">Handy for linking a signed PDF, Google Doc, or Notion page.</div>
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200 transition-colors">Cancel</button>
          <button
            type="submit"
            disabled={busy}
            data-testid="pos-submit"
            className="px-4 py-2 rounded-lg bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)] text-white font-bold text-sm inline-flex items-center gap-2 disabled:opacity-60 transition-colors"
          >
            <Save size={14} /> {busy ? "Saving…" : (isNew ? "Create position" : "Save changes")}
          </button>
        </div>
      </form>
    </div>
  );
}
